import React from 'react';
import styled, { css } from 'styled-components';
import { COLORS } from '../../../theme/Colors';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
export type ButtonSize = 'small' | 'medium' | 'large';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  children: React.ReactNode;
  as?: React.ElementType;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
  variant = 'primary',
  size = 'medium',
  fullWidth = false,
  loading = false,
  leftIcon,
  rightIcon,
  children,
  disabled,
  ...props
}, ref) => {
  return (
    <StyledButton
      ref={ref}
      $variant={variant}
      $size={size}
      $fullWidth={fullWidth}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {leftIcon && <IconWrapper $position="left">{leftIcon}</IconWrapper>}
          <ButtonContent>{children}</ButtonContent>
          {rightIcon && <IconWrapper $position="right">{rightIcon}</IconWrapper>}
        </>
      )}
    </StyledButton>
  );
});

Button.displayName = 'Button';

// Styled Components
const sizeStyles = {
  small: css`
    padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.md}`};
    font-size: ${({ theme }) => theme.fontSizes.xs};
    min-height: ${({ theme }) => theme.spacing.xl};
  `,
  medium: css`
    padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.lg}`};
    font-size: ${({ theme }) => theme.fontSizes.sm};
    min-height: ${({ theme }) => theme.spacing.xxl};
  `,
  large: css`
    padding: ${({ theme }) => `${theme.spacing.md} ${theme.spacing.xl}`};
    font-size: ${({ theme }) => theme.fontSizes.md};
    min-height: ${({ theme }) => theme.button.height};
  `,
};

const variantStyles = {
  primary: css`
    background-color: ${COLORS.primary};
    color: white;
    border: none;
    
    &:hover:not(:disabled) {
      background-color: ${COLORS.darkPurple || COLORS.primary};
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(146, 130, 203, 0.3);
    }
    
    &:active:not(:disabled) {
      transform: translateY(0);
      box-shadow: 0 2px 6px rgba(146, 130, 203, 0.3);
    }
  `,
  secondary: css`
    background-color: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.white};
    border: none;
    
    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.colors.darkPurple};
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    }
  `,
  outline: css`
    background-color: transparent;
    color: ${COLORS.primary};
    border: 2px solid ${COLORS.primary};
    
    &:hover:not(:disabled) {
      background-color: ${COLORS.primary};
      color: white;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(146, 130, 203, 0.3);
    }
  `,
  ghost: css`
    background-color: transparent;
    color: ${COLORS.primary};
    border: none;
    
    &:hover:not(:disabled) {
      background-color: rgba(146, 130, 203, 0.1);
    }
  `,
  danger: css`
    background-color: ${({ theme }) => theme.colors.errorRed};
    color: ${({ theme }) => theme.colors.white};
    border: none;
    
    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.colors.darkPurple};
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    }
  `,
};

const StyledButton = styled.button<{
  $variant: ButtonVariant;
  $size: ButtonSize;
  $fullWidth: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.xs};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  position: relative;
  overflow: hidden;
  text-decoration: none;
  font-family: inherit;
  
  ${({ $size }) => sizeStyles[$size]}
  ${({ $variant }) => variantStyles[$variant]}
  ${({ $fullWidth }) => $fullWidth && css`
    width: 100%;
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
    box-shadow: none !important;
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primaryLightTransparent};
  }
`;

const ButtonContent = styled.span`
  position: relative;
  z-index: 1;
`;

const IconWrapper = styled.span<{ $position: 'left' | 'right' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  
  ${({ $position }) => $position === 'left' && css`
    margin-right: -4px;
  `}
  
  ${({ $position }) => $position === 'right' && css`
    margin-left: -4px;
  `}
`;

const LoadingSpinner = styled.div`
  width: ${({ theme }) => theme.spacing.lg};
  height: ${({ theme }) => theme.spacing.lg};
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.radii.round};
  animation: spin 0.8s linear infinite;
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export default Button;